// Pure helpers for deriving a tag row's connectivity badge. Historical
// scrape-status callers use deriveConnectivity; the inventory page uses
// deriveLiveConnectivity over its bounded node-ID read response.

import {
  TAG_RESULT_OMITTED_ERROR_CODE,
  type PlantTagIdValue,
  type TagScrapeStateName
} from '@axilon-apps/domains-ontology-ui-state'

export type Connectivity =
  | 'healthy'
  | 'stale'
  | 'reconnecting'
  | 'error'
  | 'bad_quality'
  | 'never_scraped'
  | 'unknown'

export interface DeriveConnectivityArgs {
  /** ISO-8601 timestamp of the last successful scrape, or null/undefined if none. */
  lastScrapeTime: string | null | undefined
  /** The scrape loop's current execution state. */
  scrapeState: TagScrapeStateName | undefined
  /** Configured scrape frequency in milliseconds. */
  frequencyMs: number
  /** Optional clock injection for deterministic tests. Defaults to `Date.now()`. */
  now?: () => number
}

/**
 * Derives a UI connectivity badge from a tag's scrape status.
 *
 * Decision order matters:
 *   1. No scrape yet (no lastScrapeTime) -> never_scraped
 *   2. Scrape loop reports ERROR -> error
 *   3. Last scrape older than 2x frequency -> stale
 *   4. Otherwise -> healthy
 *
 * Rule #1 fires before #2 deliberately: a fresh tag will briefly show ERROR
 * before the first scrape even completes, and "never_scraped" is the more
 * accurate UX in that window.
 */
export function deriveConnectivity({
  lastScrapeTime,
  scrapeState,
  frequencyMs,
  now = () => Date.now()
}: DeriveConnectivityArgs): Connectivity {
  if (!lastScrapeTime) return 'never_scraped'
  if (scrapeState === 'error') return 'error'

  const lastMs = new Date(lastScrapeTime).getTime()
  if (Number.isNaN(lastMs)) {
    // Unparseable timestamp: treat as never_scraped so we don't pretend
    // staleness from a meaningless delta.
    return 'never_scraped'
  }

  // frequencyMs <= 0 would make 2x equal to 0 and force a `stale` result
  // for any positive elapsed time, which is misleading. Treat non-positive
  // frequencies as "no policy" — healthy unless we hit the error branch.
  if (frequencyMs <= 0) return 'healthy'

  const elapsed = now() - lastMs
  return elapsed > 2 * frequencyMs ? 'stale' : 'healthy'
}

/** Error codes for a source the server couldn't read right now, rather than a problem with the tag. */
const SOURCE_UNAVAILABLE_ERROR_CODES: ReadonlySet<string> = new Set([
  'TAG_SOURCE_UNAVAILABLE',
  'TAG_READER_UNAVAILABLE',
  'TAG_RESOLVER_UNAVAILABLE',
  TAG_RESULT_OMITTED_ERROR_CODE
])

/** True when the read failed because its source couldn't be read, including a failed request. */
export function isLiveSourceUnavailable(reading: PlantTagIdValue): boolean {
  return (
    reading.transportError === true ||
    (reading.errorCode !== undefined && SOURCE_UNAVAILABLE_ERROR_CODES.has(reading.errorCode))
  )
}

/**
 * Derives connectivity from the bounded node-ID live-read response used by the
 * current inventory page. Missing or unregistered rows are unknown: absence of
 * a result is not evidence that the source is offline. A source that couldn't
 * be read is unknown too; Error is left for failures of the tag's own read.
 */
export function deriveLiveConnectivity(reading: PlantTagIdValue | undefined): Connectivity {
  if (!reading || reading.unregistered || isLiveSourceUnavailable(reading)) return 'unknown'
  if (reading.errorCode === 'TAG_NO_LIVE_VALUE' || reading.error === 'no value available for tag') {
    return 'never_scraped'
  }
  if (reading.error) return 'error'
  if (reading.isGood === false) return 'bad_quality'
  if (reading.warning === 'historian_liveness_unavailable') return 'unknown'
  if (reading.stale && reading.warning === 'subscription_reconnecting') return 'reconnecting'
  if (reading.stale) return 'stale'
  return 'healthy'
}

export function connectivityLabel(c: Connectivity): string {
  switch (c) {
    case 'healthy':
      return 'Healthy'
    case 'stale':
      return 'Stale'
    case 'reconnecting':
      return 'Reconnecting'
    case 'error':
      return 'Error'
    case 'bad_quality':
      return 'Bad quality'
    case 'never_scraped':
      return 'Never scraped'
    case 'unknown':
      return 'Unknown'
  }
}

export function liveConnectivityLabel(connectivity: Connectivity): string {
  switch (connectivity) {
    case 'never_scraped':
      return 'No live data'
    case 'unknown':
      return 'Unavailable'
    default:
      return connectivityLabel(connectivity)
  }
}

export function liveConnectivityDescription(connectivity: Connectivity): string {
  switch (connectivity) {
    case 'healthy':
      return 'A current live value is available.'
    case 'stale':
      return 'The last reading is older than the expected freshness window.'
    case 'reconnecting':
      return 'The subscription is reconnecting; the last received value remains visible.'
    case 'error':
      return 'The live read failed.'
    case 'bad_quality':
      return 'The source returned a bad-quality reading.'
    case 'never_scraped':
      return 'Waiting for the first live value.'
    case 'unknown':
      return "The source couldn't be read right now."
  }
}

const CONNECTIVITY_PILL_CLASS: Record<Connectivity, string> = {
  healthy: 'bg-st-run-bg text-st-run',
  stale: 'bg-st-warn-bg text-st-warn',
  reconnecting: 'bg-st-warn-bg text-st-warn',
  error: 'bg-st-down-bg text-st-down',
  bad_quality: 'bg-st-warn-bg text-st-warn',
  never_scraped: 'bg-st-idle-bg text-st-idle',
  unknown: 'bg-st-idle-bg text-st-idle'
}

export function connectivityPillClass(connectivity: Connectivity): string {
  return CONNECTIVITY_PILL_CLASS[connectivity]
}
